import { API_API_URL, API_URL } from '@/consts';

import { ApiResponse, FormData as CaseCardData } from '../types';
import { DEFAULT_SOURCE, ZGLOSZENIE_SZKODY_OC_SOURCE } from './consts';
import { FORM_DATA_KEY_FACTORY } from './getters';
import { faker } from '@faker-js/faker';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serialize } from 'object-to-formdata';
import { useParams } from 'react-router-dom';

const deleteFile = async (hash: string, id: string) => {
  const response = await fetch(
    `${API_URL}/api/case-card/${hash}/attachments/${id}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to delete file');
  }

  return response.json();
};

export const useDeleteFileMutation = () => {
  const { id: hash } = useParams();
  return useMutation({
    mutationFn: (id: string) => deleteFile(hash ?? '', id),
  });
};

const updateFilePassword = async (
  hash: string,
  id: string,
  password: string
) => {
  const response = await fetch(
    `${API_URL}/api/case-card/${encodeURIComponent(hash ?? '')}/attachments/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify({ password }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to delete file');
  }

  return response.json();
};

export const useUpdateFilePasswordMutation = () => {
  const { id: hash } = useParams();

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      updateFilePassword(hash ?? '', id, password),
  });
};

const LEAD_ENDPOINT_BY_SOURCE: Record<string, string> = {
  [DEFAULT_SOURCE]: '/api/hf/store',
  [ZGLOSZENIE_SZKODY_OC_SOURCE]: '/api/zso/store',
};

const createLead = async (formData: FormData, endpoint: string) => {
  const response = await fetch(`${API_API_URL}${endpoint}`, {
    method: 'POST',
    body: formData,
    headers: {
      accept: 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await (response.json() as Promise<ApiResponse>);
};

export const useCreateLeadMutation = ({
  id = 38,
  source = DEFAULT_SOURCE,
  withoutEmail = false,
}: {
  id?: number;
  source?: string;
  // Phone only, like a lead from VBOT: the case card then asks for the e-mail
  // (and, for transmission easement, the plot data).
  withoutEmail?: boolean;
}) => {
  const data = {
    page: '/sankcja-kredytu/',
    referer: 'http://localhost:8000/sankcja-kredytu/',
    sent_from_url: 'http://localhost:8000/sankcja-kredytu/',
    first_name: faker.person.firstName(),
    ...(!withoutEmail && {
      email: `it+${faker.number.int({ min: 99, max: 10000 })}@helpfind.pl`,
    }),
    phone: faker.helpers.replaceSymbolWithNumber('509######'),
    agreements: [
      'Akceptuję politykę prywatności oraz regulamin świadczenia usług przez Helpfind sp. z o.o.',
    ],
    productId: id,
    ...(source !== DEFAULT_SOURCE && {
      request_params: { utm_source: source },
    }),
  };

  const formData = serialize(data);
  const endpoint =
    LEAD_ENDPOINT_BY_SOURCE[source] ?? LEAD_ENDPOINT_BY_SOURCE[DEFAULT_SOURCE];

  return useMutation({
    mutationFn: () => createLead(formData, endpoint),
  });
};

export interface CaseCardDetailsPayload {
  email?: string;
  postal_code?: string;
  postal_city_id?: number;
  plot_number?: string;
  title_register_number?: string;
}

// 422 from the details endpoint, keyed by the payload field names.
export class CaseCardDetailsValidationError extends Error {
  constructor(public readonly errors: Record<string, string[]>) {
    super('Case card details validation failed');
  }
}

const updateCaseCardDetails = async (
  hash: string,
  payload: CaseCardDetailsPayload
): Promise<CaseCardData> => {
  const response = await fetch(
    `${API_URL}/api/case-card/${encodeURIComponent(hash)}/details`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  if (response.status === 422) {
    const body = await response.json();
    throw new CaseCardDetailsValidationError(body?.errors ?? {});
  }

  if (!response.ok) {
    throw new Error('Failed to save case card details');
  }

  return response.json();
};

// The response carries the refreshed flags, so patching the cache is enough
// to close the details dialog.
export const useUpdateCaseCardDetailsMutation = () => {
  const { id: hash } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CaseCardDetailsPayload) =>
      updateCaseCardDetails(hash ?? '', payload),
    onSuccess: (data) => {
      queryClient.setQueryData<CaseCardData>(
        FORM_DATA_KEY_FACTORY(hash),
        (old) => (old ? { ...old, ...data } : data)
      );
    },
  });
};
